import { useEffect, useState } from 'react'
import { Modal } from '@/components/new/Modal'
import {
  ShipmentNotification,
  ShipmentNotificationParams,
  ShipmentNotificationStatus,
} from '@/interfaces/shipmentNotification'
import { useAsyncProviders, useAsyncManagers } from '../hooks/useAsyncSearch'
import { statusOptions } from '../filters/constants/statusOptions'
import { DateFilter } from '@/containers/billing/register/billingFilters/filter/DateFilter'
import { useForm, FieldValues } from 'react-hook-form'
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { InputForm } from '@/components/new/form/InputForm'
import { TextAreaForm } from '@/components/new/form/TextAreaForm'
import { ButtonPrimary } from '@/components/new/buttons/ButtonPrimary'
import { SelectFormAsync } from '@/components/forms/SelectFormAsync'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface NewShipmentNotificationModalProps {
  isOpen: boolean
  onClose: () => void
  notificationToEdit?: ShipmentNotification
  onSave: (_data: ShipmentNotificationParams) => void
  isLoading?: boolean
}

export default function NewShipmentNotificationModal({
  isOpen,
  onClose,
  notificationToEdit,
  onSave,
  isLoading = false,
}: NewShipmentNotificationModalProps) {
  const isEditing = !!notificationToEdit
  // Mapear notificationToEdit a ShipmentNotificationParams
  const mapToParams = (
    notif?: ShipmentNotification
  ): ShipmentNotificationParams => {
    let shouldArrivedAt = notif?.shouldArrivedAt ?? ''
    // Si viene en formato ISO (con T y hora), extraer solo la fecha
    if (shouldArrivedAt.includes('T')) {
      shouldArrivedAt = shouldArrivedAt.split('T')[0]
    }
    return {
      name: notif?.name ?? '',
      shouldArrivedAt,
      providerCompanyId: notif?.providerCompanyId ?? 0,
      managerCompanyId: notif?.managerCompanyId ?? 0,
      observations: notif?.observations ?? '',
      status: (notif?.status as ShipmentNotificationStatus) ?? 'CREATED',
    }
  }
  const form = useForm<FieldValues>({
    defaultValues: isEditing
      ? mapToParams(notificationToEdit)
      : {
          name: '',
          shouldArrivedAt: '',
          providerCompanyId: 0,
          managerCompanyId: 0,
          observations: '',
          status: 'CREATED',
        },
    mode: 'onChange',
  })
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = form

  // Hooks para búsqueda asíncrona
  const { searchProviders } = useAsyncProviders()
  const { searchManagers } = useAsyncManagers()

  // Reset form when modal abre/cierra o cambia notificación
  useEffect(() => {
    if (!isOpen) {
      reset({
        name: '',
        shouldArrivedAt: '',
        providerCompanyId: 0,
        managerCompanyId: 0,
        observations: '',
        status: 'CREATED',
      })
    } else if (notificationToEdit) {
      reset(mapToParams(notificationToEdit))
    }
  }, [isOpen, notificationToEdit, reset])
  // Validar fecha de arribo
  const validateDate = (date: string): boolean => {
    if (!date) return false
    try {
      // Parsear la fecha manualmente para evitar problemas de zona horaria
      const dateParts = date.split('-')
      if (dateParts.length !== 3) return false

      const [year, month, day] = dateParts.map(Number)
      if (!year || !month || !day) return false

      const selectedDate = new Date(year, month - 1, day)
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      return selectedDate >= today
    } catch {
      return false
    }
  }
  const onSubmit = (data: FieldValues) => {
    const d = data as ShipmentNotificationParams

    // Solo validar fecha de arribo futura si NO estamos editando
    if (!isEditing && !validateDate(d.shouldArrivedAt)) {
      form.setError('shouldArrivedAt', {
        type: 'manual',
        message: 'La fecha de arribo debe ser mayor o igual a la fecha actual',
      })
      return
    }

    // Asegurar que la fecha se maneje correctamente sin problemas de zona horaria
    let processedDate = d.shouldArrivedAt
    if (processedDate) {
      // Si la fecha viene en formato YYYY-MM-DD, asegurar que se mantenga así
      const dateParts = processedDate.split('-')
      if (dateParts.length === 3) {
        const [year, month, day] = dateParts
        // Recrear la fecha en formato YYYY-MM-DD para evitar problemas de zona horaria
        processedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
    }

    // Limpiar datos
    const cleanData: ShipmentNotificationParams = {
      name: d.name?.trim() || '',
      shouldArrivedAt: processedDate,
      providerCompanyId: d.providerCompanyId,
      managerCompanyId: d.managerCompanyId,
    }
    if (d.observations?.trim()) cleanData.observations = d.observations.trim()
    if (isEditing && d.status)
      cleanData.status = d.status as ShipmentNotificationStatus

    onSave(cleanData)
  }
  // Para DateFilter
  const [selectedDateType, setSelectedDateType] = useState('emision')
  return (
    <Modal
      confirmOnClose={false}
      isOpen={isOpen}
      onClose={onClose}
      size="m"
      subtitle="Complete los campos para crear o editar una notificación de embarque."
      title={
        isEditing
          ? 'Editar notificación de embarque'
          : 'Crear notificación de embarque'
      }
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex h-[60dvh] flex-col">
            <div className="flex-1 space-y-5 overflow-y-auto">
              <InputForm
                control={control}
                label="Nombre"
                name="name"
                placeholder="Nombre..."
                rules={{ required: 'El nombre es requerido' }}
              />
              <FormField
                control={control}
                name="providerCompanyId"
                render={() => (
                  <SelectFormAsync
                    disabled={isLoading}
                    label="Proveedor"
                    name="providerCompanyId"
                    onSearch={searchProviders}
                    placeholder="Seleccione un proveedor"
                  />
                )}
                rules={{
                  required: 'Debe seleccionar un proveedor',
                  validate: (value) =>
                    value > 0 || 'Debe seleccionar un proveedor',
                }}
              />
              <FormField
                control={control}
                name="managerCompanyId"
                render={() => (
                  <SelectFormAsync
                    disabled={isLoading}
                    label="Operador Logístico"
                    name="managerCompanyId"
                    onSearch={searchManagers}
                    placeholder="Seleccione un operador logístico"
                  />
                )}
                rules={{
                  required: 'Debe seleccionar un operador logístico',
                  validate: (value) =>
                    value > 0 || 'Debe seleccionar un operador logístico',
                }}
              />
              <FormField
                control={control}
                name="shouldArrivedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Fecha de arribo{' '}
                      <span
                        className={`font-bold ${errors.shouldArrivedAt ? 'text-red-600' : 'text-black'}`}
                      >
                        (*)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <DateFilter
                        buttonClassName="w-full"
                        dateFrom={field.value || ''}
                        dateTo={null}
                        hideDateTypeSelect
                        onDateRangeChange={(from) => {
                          field.onChange(from || '')
                          setValue('shouldArrivedAt', from || '')
                        }}
                        onDateTypeChange={setSelectedDateType}
                        onlySpecific
                        selectedDateType={selectedDateType}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'La fecha de arribo es requerida' }}
              />
              {isEditing && (
                <FormField
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Estado <span className="font-bold text-black">(*)</span>
                      </FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger className="transition-colors hover:border-gray-400">
                            <SelectValue placeholder="Seleccione un estado" />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions
                              .filter((option) => option.value !== 'all')
                              .map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage>
                        {errors.status?.message as string}
                      </FormMessage>
                    </FormItem>
                  )}
                  rules={{ required: 'El estado es requerido' }}
                />
              )}
              {/* Observaciones debe mostrarse tanto en creación como en edición */}
              <div className="mb-4 pb-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Observaciones
                </label>
                <TextAreaForm
                  control={control}
                  label={undefined}
                  name="observations"
                  placeholder="Observaciones..."
                  rules={{ required: false }}
                />
              </div>
            </div>
            <div className="mt-4 flex w-full justify-between border-t pt-4">
              <Button
                onClick={() => {
                  onClose()
                  reset()
                }}
                type="button"
                variant="outline"
              >
                Cancelar
              </Button>
              <ButtonPrimary
                className=""
                onClick={handleSubmit(onSubmit)}
                text={
                  isLoading
                    ? 'Guardando...'
                    : isEditing
                      ? 'Actualizar'
                      : 'Guardar'
                }
                type="submit"
              />
            </div>
          </div>
        </form>
      </Form>
    </Modal>
  )
}
